import { Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

export function PageHeader({ title, subtitle, children, testid }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6" data-testid={testid}>
      <div>
        <h1 className="font-display font-extrabold text-2xl lg:text-3xl text-bk-blue">{title}</h1>
        {subtitle && <p className="text-sm text-slate-500 mt-0.5">{subtitle}</p>}
      </div>
      {children && <div className="flex items-center gap-2 flex-wrap">{children}</div>}
    </div>
  );
}

export function StatCard({ label, value, icon: Icon, tone = "green", sub, testid, onClick }) {
  const tones = {
    green: "text-bk-green bg-bk-lightGreen",
    blue: "text-bk-blue bg-blue-50",
    info: "text-bk-info bg-sky-50",
    warning: "text-amber-600 bg-amber-50",
    danger: "text-bk-danger bg-red-50",
    slate: "text-slate-600 bg-slate-100",
  };
  return (
    <Card
      onClick={onClick}
      data-testid={testid}
      className={`p-4 lg:p-5 border-border shadow-sm hover:shadow-md transition-shadow ${onClick ? "cursor-pointer" : ""}`}
    >
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</p>
          <p className="font-display font-extrabold text-2xl lg:text-[26px] text-slate-800 mt-1 tabular-nums">{value}</p>
          {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
        </div>
        {Icon && (
          <div className={`h-10 w-10 rounded-lg flex items-center justify-center shrink-0 ${tones[tone]}`}>
            <Icon className="h-5 w-5" />
          </div>
        )}
      </div>
    </Card>
  );
}

const STATUS_MAP = {
  present: "bg-bk-lightGreen text-bk-green border-green-200",
  completed: "bg-blue-50 text-blue-700 border-blue-200",
  active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  paused: "bg-amber-50 text-amber-700 border-amber-200",
  discontinued: "bg-rose-50 text-rose-700 border-rose-200",
  archived: "bg-slate-100 text-slate-500 border-slate-200",
  approved: "bg-bk-lightGreen text-bk-green border-green-200",
  paid: "bg-bk-lightGreen text-bk-green border-green-200",
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  pending_verification: "bg-amber-50 text-amber-700 border-amber-200",
  scheduled: "bg-sky-50 text-bk-info border-sky-200",
  started: "bg-sky-50 text-bk-info border-sky-200",
  booked: "bg-sky-50 text-bk-info border-sky-200",
  draft: "bg-slate-100 text-slate-600 border-slate-200",
  rescheduled: "bg-indigo-50 text-indigo-700 border-indigo-200",
  cancelled: "bg-slate-100 text-slate-500 border-slate-200",
  absent: "bg-red-50 text-bk-danger border-red-200",
  rejected: "bg-red-50 text-bk-danger border-red-200",
  unavailable: "bg-red-50 text-bk-danger border-red-200",
  partially_settled: "bg-orange-50 text-orange-600 border-orange-200",
  partially_paid: "bg-orange-50 text-orange-600 border-orange-200",
  disputed: "bg-red-50 text-bk-danger border-red-200",
};

export function StatusBadge({ status, source }) {
  const label = source === "AUTOMATIC" ? "Auto Absent" : (status || "").replace(/_/g, " ");
  const cls = STATUS_MAP[status] || "bg-slate-100 text-slate-600 border-slate-200";
  return (
    <Badge variant="outline" className={`capitalize font-medium ${cls}`}>
      {label}
    </Badge>
  );
}

export function Loading({ label = "Loading..." }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-slate-400">
      <Loader2 className="h-7 w-7 animate-spin mb-2" />
      <p className="text-sm">{label}</p>
    </div>
  );
}

export function EmptyState({ icon: Icon, title, subtitle }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      {Icon && <Icon className="h-10 w-10 text-slate-300 mb-3" />}
      <p className="font-semibold text-slate-600">{title}</p>
      {subtitle && <p className="text-sm text-slate-400 mt-1 max-w-md">{subtitle}</p>}
    </div>
  );
}

export function AccountBalanceBadge({ balance, className = "" }) {
  const bal = Number(balance || 0);
  if (bal > 0) {
    return (
      <Badge variant="outline" className={`bg-emerald-50 text-emerald-700 border-emerald-200 font-semibold tabular-nums ${className}`}>
        Credit ₹{bal.toLocaleString("en-IN")}
      </Badge>
    );
  }
  if (bal < 0) {
    return (
      <Badge variant="outline" className={`bg-rose-50 text-rose-700 border-rose-200 font-semibold tabular-nums ${className}`}>
        Due ₹{Math.abs(bal).toLocaleString("en-IN")}
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className={`bg-slate-50 text-slate-500 border-slate-200 font-medium ${className}`}>
      Settled
    </Badge>
  );
}

